import { getApiOrigin } from "@/lib/utils.ts";
import { useThreadSocket } from "@/components/messages/hooks/useThreadSocket.ts";
import { errorToast } from "@/components/ui/toast.tsx";
import { api } from "@/lib/http.ts";
import { apiErrorMessage } from "@/shared/apiErrorMessage.ts";
import { isCancellation } from "@/shared/retryPolicy.ts";
import { savedBrowserTaskQueries } from "@/shared/useSavedBrowserTasks.ts";
import {
  browserRestartCopy,
  isKernelLiveViewUrl,
  isPrivateBrowserViewUrl,
  shouldCheckBrowserTakeover,
  requestBrowserConnection,
  browserConnectionAttemptUrl,
} from "@/shared/browserTakeover.ts";
import type { BrowserSavedTaskActionReq } from "@/types/browserSavedTask.ts";
import type {
  BrowserLiveViewResp,
  BrowserTakeoverStatusResp,
  BrowserTakeoverSurface,
  BrowserConnectionEvent,
  BrowserConnectionRequest,
} from "@/types/browserTakeover.ts";
import type { GetThreadResp } from "@/types/thread.ts";
import {
  keepPreviousData,
  queryOptions,
  skipToken,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useCallback, useEffect, useRef } from "react";

/** Frames arrive every ~1.5 s while okthx browses; one status read per frame would be far too many. */
const FRAME_REFRESH_INTERVAL_MS = 10_000;

const takeoverPath = (threadId: number) => `/api/a/threads/${threadId}/browser-takeover` as const;

/** A coarse pointer or a narrow window is the phone layout, which the server sizes for. */
const takeoverSurface = (): BrowserTakeoverSurface =>
  window.matchMedia("(pointer: coarse), (max-width: 640px)").matches ? "ios" : "web";

export const browserTakeoverQueries = {
  all: (threadId: number) => ["threads", threadId, "browserTakeover"] as const,
  /**
   * Keyed by the thread's activity as well: pausing for a card, finishing, and a request the
   * worker never acted on all change the activity without a takeover event, and each needs a
   * fresh read.
   */
  status: (thread: Pick<GetThreadResp, "id" | "activityState">, enabled: boolean) =>
    queryOptions({
      queryKey: [...browserTakeoverQueries.all(thread.id), thread.activityState] as const,
      queryFn: enabled
        ? async ({ signal }) => {
            const response = await api.get<BrowserTakeoverStatusResp>(takeoverPath(thread.id), {
              signal,
            });
            return response.data;
          }
        : skipToken,
      // The bar keeps its last state while the next activity's read is in flight.
      placeholderData: keepPreviousData,
      retry: false,
      // This hook owns the focus re-read; see the window listener in `useBrowserTakeover`.
      refetchOnWindowFocus: false,
    }),
};

/**
 * Who drives the thread's browser, and the calls that hand it between okthx and the user.
 *
 * Check active and waiting threads so control remains available throughout a browser task.
 */
export function useBrowserTakeover(thread: GetThreadResp) {
  const enabled = shouldCheckBrowserTakeover(thread.activityState);
  const queryClient = useQueryClient();
  const connectionRequest = useRef<AbortController | undefined>(undefined);
  const cancelConnection = useCallback(() => connectionRequest.current?.abort(), []);
  useEffect(() => cancelConnection, [cancelConnection]);
  const startConnection = () => {
    cancelConnection();
    const controller = new AbortController();
    connectionRequest.current = controller;
    return controller.signal;
  };
  const statusQuery = browserTakeoverQueries.status(thread, enabled);
  const { data } = useQuery(statusQuery);
  const status = enabled ? data : undefined;

  const refresh = useCallback(
    async () =>
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: browserTakeoverQueries.all(thread.id) }),
        queryClient.invalidateQueries({ queryKey: savedBrowserTaskQueries.all(thread.id) }),
      ]),
    [queryClient, thread.id],
  );
  // Show a change's answer at once, then re-read: a slow answer can land after a later
  // change (a /take answer after its own Cancel) and would otherwise leave the bar stale.
  const setStatus = (next: BrowserTakeoverStatusResp) => {
    queryClient.setQueryData(statusQuery.queryKey, next);
    void refresh();
  };

  useThreadSocket(thread.id, "thread:browser_takeover", () => {
    void refresh();
  });
  // A browser task that starts mid-turn raises no takeover event; its frames are the first sign.
  const lastFrameRefreshMs = useRef(0);
  useThreadSocket(thread.id, "thread:browser_frame", () => {
    if (status?.state !== "unavailable") return;
    const now = Date.now();
    if (now - lastFrameRefreshMs.current < FRAME_REFRESH_INTERVAL_MS) return;
    lastFrameRefreshMs.current = now;
    void refresh();
  });

  // A refusal usually means the takeover moved on (409/410), so say why and re-read it.
  // A cancellation is not a refusal. `isCancellation` matches on `name`/`code` because a
  // cancellation does not arrive as one consistent class: axios raises `CanceledError`, an
  // abort raises a DOMException whose relationship to `Error` varies by runtime, and a
  // signal lacking the API raises a `TypeError`. An `instanceof Error` test caught none of
  // the shapes that mattered here (BIG-8995).
  const onRefused = (fallback: string) => (error: unknown) => {
    if (isCancellation(error)) return;
    errorToast(apiErrorMessage(error) ?? fallback);
    void refresh();
  };

  const take = useMutation({
    mutationFn: async (taskId: string) => {
      const response = await api.post<BrowserTakeoverStatusResp>(
        `${takeoverPath(thread.id)}/take`,
        {
          taskId,
          surface: takeoverSurface(),
        },
      );
      return response.data;
    },
    onSuccess: setStatus,
    onError: onRefused("Couldn’t take control of the browser."),
  });

  const handBack = useMutation({
    mutationFn: async (controlId: string) => {
      const response = await api.post<BrowserTakeoverStatusResp>(
        `${takeoverPath(thread.id)}/hand-back`,
        { controlId },
      );
      return response.data;
    },
    onSuccess: setStatus,
    onError: onRefused("Couldn’t hand the browser back."),
  });

  const end = useMutation({
    mutationFn: async (controlId: string) => {
      await api.post(`${takeoverPath(thread.id)}/end`, { controlId });
    },
    onSuccess: () => setStatus({ state: "unavailable" }),
    onError: onRefused("Couldn’t end the task."),
  });

  const liveView = useMutation({
    mutationFn: async (input: BrowserConnectionRequest) => {
      const controlId = typeof input === "string" ? input : input.id;
      const signal = startConnection();
      const { value: response, connection } = await requestBrowserConnection(
        (signal) =>
          api.post<BrowserLiveViewResp>(
            `${takeoverPath(thread.id)}/live-view`,
            {
              controlId,
              interactiveViewer: true,
              surface: takeoverSurface(),
            },
            { signal },
          ),
        (event) => {
          if (status?.taskId) connectionLog.mutate({ ...event, taskId: status.taskId });
        },
        signal,
        typeof input === "string" ? undefined : input.connection,
      );
      // Checked rather than trusted: the link goes straight into a frame, and it controls a
      // browser that may hold the user's sign-ins.
      const { url, windowUrl } = response.data;
      const viewerUrl = windowUrl ?? response.data.viewerUrl;
      if (viewerUrl) {
        if (!isPrivateBrowserViewUrl(viewerUrl, getApiOrigin()))
          throw new Error("Invalid private browser view");
        return browserConnectionAttemptUrl(
          new URL(viewerUrl, getApiOrigin()).toString(),
          connection,
        );
      }
      if (!isKernelLiveViewUrl(url)) throw new Error("The live view link is not Kernel's");
      return url;
    },
    onError: onRefused("Couldn’t open the browser."),
  });

  const watch = useMutation({
    mutationFn: async (input: BrowserConnectionRequest) => {
      const taskId = typeof input === "string" ? input : input.id;
      const signal = startConnection();
      const { value: response, connection } = await requestBrowserConnection(
        (signal) =>
          api.post<BrowserLiveViewResp>(
            `${takeoverPath(thread.id)}/watch`,
            { taskId, interactiveViewer: true },
            { signal },
          ),
        (event) => connectionLog.mutate({ ...event, taskId }),
        signal,
        typeof input === "string" ? undefined : input.connection,
      );
      const url = response.data.windowUrl;
      if (!url || !isPrivateBrowserViewUrl(url, getApiOrigin()))
        throw new Error("Invalid browser window");
      return browserConnectionAttemptUrl(new URL(url, getApiOrigin()).toString(), connection);
    },
    onError: onRefused("Couldn’t open the browser."),
  });
  const endTask = useMutation({
    mutationFn: async (taskId: string) => {
      await api.post(`${takeoverPath(thread.id)}/end-task`, { taskId });
    },
    onSuccess: () => setStatus({ state: "unavailable" }),
    onError: onRefused("Couldn’t end the task."),
  });
  const connectionLog = useMutation({
    mutationFn: async (event: BrowserConnectionEvent & { taskId: string }) => {
      await api.post(`${takeoverPath(thread.id)}/connection`, event);
    },
  });
  /**
   * Resumes the saved request on a new browser. This goes through `resume`, not `open`, so no
   * live-view link is minted and the step-up gate that guards manual control does not apply.
   */
  const restart = useMutation({
    mutationFn: async (target: NonNullable<BrowserTakeoverStatusResp["restart"]>) => {
      const body: BrowserSavedTaskActionReq = {
        revision: target.revision,
        requestId: crypto.randomUUID(),
        surface: takeoverSurface(),
      };
      await api.post(`/api/a/threads/${thread.id}/browser-tasks/${target.taskId}/resume`, body);
    },
    // Awaited so the mutation stays pending until the status has been re-read. Clearing
    // `isPending` on the POST alone re-enables the button against the revision it already
    // spent, and the second press answers 409 on a restart that worked.
    onSuccess: async () => {
      await refresh();
    },
    onError: onRefused(browserRestartCopy.failure),
  });
  // A tab left open for days can hold a socket the server already dropped, so the bar
  // re-reads when the window comes back. This is why the status query turns
  // `refetchOnWindowFocus` off: TanStack listens on `visibilitychange`, which does not fire
  // when the user switches apps, and leaving both on raced two reads against each other.
  useEffect(() => {
    if (!enabled) return;
    const onFocus = () => void refresh();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [enabled, refresh]);
  return {
    status,
    take,
    handBack,
    end,
    endTask,
    liveView,
    watch,
    connectionLog,
    restart,
    refresh,
    cancelConnection,
  };
}
